import React, { useEffect, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { MeasurementInputType, ValueType } from '../../app/types';
import MeasurementInputTypeInput from '../inputs/MeasurementInputTypeInput';
import ValueTypeInput from '../inputs/ValueTypeInput';
import { Measurement, MeasurementUpdate } from './types';

interface EditMeasurementProps {
  buttonLabel?: string;
  measurement?: Measurement;
  onSave?: (m: MeasurementUpdate) => void;
}

function EditMeasurement(props: EditMeasurementProps) {
  const { measurement, onSave, buttonLabel } = props;

  const [name, setName] = useState<string>(measurement?.name || '');
  const [active, setActive] = useState<boolean>(measurement?.active || true);
  const [inputType, setInputType] = useState<MeasurementInputType>(measurement?.inputType || MeasurementInputType.EntryInput);
  const [type, setType] = useState<ValueType>(measurement?.type || ValueType.Float);
  const [order, setOrder] = useState<number | ''>(measurement?.order || 0);
  const [validIntegers, setValidIntegers] = useState<string>('');
  const [prompt, setPrompt] = useState<string>(measurement?.prompt || '');

  useEffect(() => {
    if (measurement) {
      setName(measurement.name || '');
      setOrder(measurement.order);
      const loadedMeasurementType = measurement?.type || 0;
      if (loadedMeasurementType in ValueType) {
        setType(loadedMeasurementType);
      }
      const loadedInputType = measurement?.inputType || 0;
      if (loadedInputType in MeasurementInputType) {
        setInputType(loadedInputType);
      }
      if (measurement.active !== undefined) {
        setActive(measurement.active);
      }
      if (!measurement.validIntegers) {
        setValidIntegers('');
      } else {
        setValidIntegers(measurement.validIntegers.join(','));
      }
    }
  }, [measurement]);

  type ChangeEvent = (e: React.ChangeEvent<HTMLInputElement>) => void;
  const onNameChanged: ChangeEvent = e => setName(e.target.value);
  const onActiveChanged: ChangeEvent = e => setActive(e.target.checked);
  const onTypeChanged = (v: ValueType) => setType(v);
  const onInputTypeChanged = (v: MeasurementInputType) => setInputType(v);
  const onValidIntegersChanged: ChangeEvent = e => setValidIntegers(e.target.value);
  const onOrderChanged: ChangeEvent = e => {
    const value = parseInt(e.target.value);
    setOrder(isNaN(value) ? '' : value);
  }
  const onPromptChanged: ChangeEvent = e => setPrompt(e.target.value);

  const onSaveClicked = () => {
    const newMeasurement: MeasurementUpdate = {
      ...measurement,
      name,
      active,
      type,
      inputType,
      prompt,
    };
    if (order !== '') {
      newMeasurement.order = order;
    }
    try {
      newMeasurement.validIntegers = (validIntegers || '')
        .split(',')
        .map(n => parseInt(n))
        .filter(n => !isNaN(n));
    } catch (err) {
      console.log('Error parsing valid integers', err);
    }
    if (onSave) {
      onSave(newMeasurement);
    }
  }

  return (
    <>
      <Form>
        {measurement && (
          <Form.Group as={Row} controlId="measurementID" className="mb-3">
            <Form.Label column sm="2">ID</Form.Label>
            <Col sm="10">
              <code>{measurement.id}</code>
            </Col>
          </Form.Group>
        )}
        <Form.Group as={Row} controlId="measurementName" className="mb-3">
          <Form.Label column sm="2">Name</Form.Label>
          <Col sm="10">
            <Form.Control type="text" placeholder="" value={name} onChange={onNameChanged} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="measurementInputType" className="mb-3">
          <Form.Label column sm="2">Input Type</Form.Label>
          <Col sm="10">
            <MeasurementInputTypeInput value={inputType} onChange={onInputTypeChanged} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="measurementType" className="mb-3">
          <Form.Label column sm="2">Type</Form.Label>
          <Col sm="10">
            <ValueTypeInput value={type} onChange={onTypeChanged} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="measurementActive" className="mb-3">
          <Form.Label column sm="2">Active</Form.Label>
          <Col sm="10">
            <Form.Check type="checkbox" checked={active} onChange={onActiveChanged} />
          </Col>
        </Form.Group>
        {(type === ValueType.Integer) && (
          <Form.Group as={Row} controlId="measurementValidIntegers" className="mb-3">
            <Form.Label column sm="2">Valid Integers</Form.Label>
            <Col sm="10">
              <Form.Control type="text" placeholder="Comma-separated values..." value={validIntegers} onChange={onValidIntegersChanged} />
            </Col>
          </Form.Group>
        )}
        <Form.Group as={Row} controlId="measurementOrder" className="mb-3">
          <Form.Label column sm="2">Order</Form.Label>
          <Col sm="10">
            <Form.Control type="number" value={order} onChange={onOrderChanged} />
          </Col>
        </Form.Group>
        <Form.Group as={Row} controlId="measurementPrompt" className="mb-3">
          <Form.Label column sm="2">Prompt</Form.Label>
          <Col sm="10">
            <Form.Control type="text" placeholder="" value={prompt} onChange={onPromptChanged} />
          </Col>
        </Form.Group>
        <button type="button" className="btn btn-primary" onClick={onSaveClicked}>
          {buttonLabel || "Save measurement"}
        </button>
      </Form>
    </>
  );
}

export default EditMeasurement;